<script setup>
import { ref, onMounted } from 'vue';
import { Head } from '@inertiajs/vue3';
import axios from 'axios';

const items = ref([]);
const loading = ref(true);

const loadData = async () => {
    loading.value = true;
    try {
        const response = await axios.get('/admin/seo-meta-checker/data');
        if (response.data.status === 'success') {
            items.value = response.data.data;
        }
    } catch (error) {
        console.error('Error:', error);
    } finally {
        loading.value = false;
    }
};

const getStatusColor = (status) => {
    return status === 'OK' ? 'success' : 'danger';
};

const getStatusIcon = (status) => {
    return status === 'OK' ? 'fa fa-check-circle' : 'fa fa-exclamation-circle';
};

onMounted(() => {
    loadData();
});
</script>

<template>
    <Head title="SEO Meta Checker" />

    <div class="container-full">
        <div class="content-header">
            <div class="d-flex align-items-center">
                <div class="me-auto">
                    <h3 class="page-title">SEO Meta Checker</h3>
                    <nav>
                        <ol class="breadcrumb">
                            <li class="breadcrumb-item"><a href="/admin/dashboard"><i class="mdi mdi-home-outline"></i></a></li>
                            <li class="breadcrumb-item active">SEO Meta Checker</li>
                        </ol>
                    </nav>
                </div>
            </div>
        </div>

        <section class="content">
            <div class="row">
                <div class="col-12">
                    <div class="card">
                        <div class="card-header">
                            <h4 class="card-title">Status Meta Data Website</h4>
                            <div class="card-controls">
                                <button class="btn btn-sm btn-outline-primary" @click="loadData">
                                    <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i> Refresh
                                </button>
                            </div>
                        </div>
                        <div class="card-body">
                            <div class="table-responsive">
                                <table class="table table-hover">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Type</th>
                                            <th>Title</th>
                                            <th>Meta Title</th>
                                            <th>Meta Description</th>
                                            <th>Meta Keywords</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-if="loading">
                                            <td colspan="7" class="text-center p-5">
                                                <div class="spinner-border text-primary"></div>
                                            </td>
                                        </tr>
                                        <tr v-else-if="items.length === 0">
                                            <td colspan="7" class="text-center p-5">Tidak ada data</td>
                                        </tr>
                                        <tr v-else v-for="(item, index) in items" :key="index">
                                            <td>{{ index + 1 }}</td>
                                            <td><span class="badge badge-info">{{ item.type }}</span></td>
                                            <td>
                                                <div class="text-truncate" style="max-width: 200px;" :title="item.title">
                                                    <a :href="item.admin_url" class="text-bold text-primary">
                                                        {{ item.title }} <i class="fa fa-pencil-square-o ms-1 small"></i>
                                                    </a>
                                                    <div class="small text-muted mt-1" v-if="item.url && item.url !== '#'">
                                                        <a :href="item.url" target="_blank" class="text-muted"><i class="fa fa-external-link me-1"></i> Preview</a>
                                                    </div>
                                                </div>
                                            </td>
                                            
                                            <!-- Meta Title -->
                                            <td>
                                                <div v-if="item.meta_title" class="mb-1 text-truncate" style="max-width: 250px;" :title="item.meta_title">
                                                    {{ item.meta_title }}
                                                </div>
                                                <div v-if="item.analysis && item.analysis.title_status">
                                                    <span v-for="(status, sIdx) in item.analysis.title_status" :key="sIdx" 
                                                          :class="'badge badge-' + status.type + ' me-1'">
                                                        {{ status.label }}
                                                    </span>
                                                </div>
                                            </td>

                                            <!-- Meta Description -->
                                            <td>
                                                <div v-if="item.meta_description" class="mb-1 text-truncate" style="max-width: 300px;" :title="item.meta_description">
                                                    {{ item.meta_description }}
                                                </div>
                                                <div v-if="item.analysis && item.analysis.desc_status">
                                                    <span v-for="(status, sIdx) in item.analysis.desc_status" :key="sIdx" 
                                                          :class="'badge badge-' + status.type + ' me-1'">
                                                        {{ status.label }}
                                                    </span>
                                                </div>
                                            </td>

                                            <!-- Meta Keywords -->
                                            <td>
                                                <div v-if="item.meta_keywords === null">
                                                    -
                                                </div>
                                                <div v-else-if="!item.meta_keywords">
                                                    <span class="badge badge-danger">Kosong</span>
                                                </div>
                                                <div v-else>
                                                    <div class="text-truncate" style="max-width: 200px;" :title="item.meta_keywords">
                                                        {{ item.meta_keywords }}
                                                    </div>
                                                    <span class="badge badge-success mt-1">OK</span>
                                                </div>
                                            </td>

                                            <td>
                                                <span :class="'badge badge-' + getStatusColor(item.status)">
                                                    {{ item.status }}
                                                </span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>
